import React, { useEffect, useState } from "react";
import { Modal, ModalBody, ModalHeader } from "reactstrap";
import ReactSelect from "react-select";
import BackButton from "../../../components/custom/BackButton";
import { useAppContext } from "../../../context";
import { SelectOption } from "../../../globals/interfaces";
import { AttributeValue, EntityType, UnitDetail, UnitStatus } from "./types";
import {
    Block,
    ButtonRow,
    Checkbox,
    CheckboxRow,
    CheckboxText,
    ErrorText,
    FieldLabel,
    GhostButton,
    HeaderContainer,
    HistoryItem,
    HistoryList,
    HistoryMeta,
    HistoryText,
    Input,
    InlineForm,
    PrimaryButton,
    RequiredMark,
    Select,
    StatusBadge,
    Tab,
    TabsNav,
} from "./styles";
import useEmployees from "../../dev/Inventory/useEmployees";
import moment from "moment";
import _ from "lodash";
import { useSerialsModalDataStore } from "./SerialsModal";

const EVENT_LABELS: Record<string, string> = {
    added: 'Added',
    issued: 'Issued',
    returned: 'Returned',
    transferred: 'Transferred',
    repair_started: 'In repair',
    repaired: 'Repaired',
    damaged: 'Damaged',
    discarded: 'Discarded',
};

interface UnitDetailModalProps {
    modal: boolean;
    setModal: (open: boolean) => void;
    unit: UnitDetail | null;
    onAssign: (entityType: EntityType, entityId: string) => Promise<void>;
    onRevoke: () => Promise<void>;
    onSetStatus: (toStatus: UnitStatus, reasonCategory?: string, note?: string, keepHolder?: boolean) => Promise<void>;
    onPatch: (assetTag: string | null, attributes: Record<string, AttributeValue>) => Promise<void>;
}

type StatusAction = "in_repair" | "damaged" | "discarded" | "available";
type DetailTab = "attributes" | "actions" | "history";

export default function UnitDetailModal({ modal, setModal, unit, onAssign, onRevoke, onSetStatus, onPatch }: UnitDetailModalProps) {
    const { user } = useAppContext();
    const stations = user.authorizedUnits;
    const { employees } = useEmployees();

    const [detailTab, setDetailTab] = useState<DetailTab>("attributes");
    const [showAssign, setShowAssign] = useState(false);
    const [assignEntityType, setAssignEntityType] = useState<EntityType>("employee");
    const [assignTarget, setAssignTarget] = useState<SelectOption | null>(null);

    const [pendingAction, setPendingAction] = useState<StatusAction | null>(null);
    const [reasonCategory, setReasonCategory] = useState("");
    const [note, setNote] = useState("");
    const [keepHolder, setKeepHolder] = useState(true);

    const [assetTagInput, setAssetTagInput] = useState("");
    const [attrValues, setAttrValues] = useState<Record<string, AttributeValue>>({});
    const [assetTagError, setAssetTagError] = useState("");
    const [saveError, setSaveError] = useState("");

    const refreshSerials = useSerialsModalDataStore(s => s.refreshSerials);

    const toggle = () => setModal(!modal);

    useEffect(() => {
        if (modal) {
            setDetailTab("attributes");
            setShowAssign(false);
            setAssignEntityType("employee");
            setAssignTarget(null);
            setPendingAction(null);
            setReasonCategory("");
            setNote("");
            setKeepHolder(true);
        }
        if (modal && unit) {
            setAssetTagInput(unit.assetTag ?? "");
            const initial: Record<string, AttributeValue> = {};
            (unit.attributeFields ?? []).forEach((f) => {
                initial[f.attributeKey] = f.value ?? (f.dataType === "bool" ? false : "");
            });
            setAttrValues(initial);
            setAssetTagError("");
            setSaveError("");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modal, unit?.serial]);

    if (!unit) return null;

    const setAttrValue = (key: string, value: AttributeValue) => setAttrValues((prev) => ({ ...prev, [key]: value }));

    const saveAttributes = async () => {
        const attributes: Record<string, AttributeValue> = {};
        (unit.attributeFields ?? []).forEach((f) => {
            attributes[f.attributeKey] = attrValues[f.attributeKey];
        });
        const assetTag = assetTagInput.trim() === "" ? null : assetTagInput.trim();
        setAssetTagError("");
        setSaveError("");
        try {
            await onPatch(assetTag, attributes);
            refreshSerials();
        } catch (err: any) {
            setAssetTagError("Asset tag already in use.");
            // if (err?.response?.status === 409) {
            //     setAssetTagError(err?.response?.data?.message ?? "Asset tag already in use.");
            // } else {
            //     setSaveError("Save failed.");
            // }
        }
    };

    const employeeOptions: SelectOption[] = employees.map((e) => ({ value: e.s_email, label: `${e.s_first_name} ${e.s_last_name}` }));
    const unitOptions: SelectOption[] = stations.map((s) => ({ value: s, label: s }));
    const targetOptions = assignEntityType === "employee" ? employeeOptions : unitOptions;
    const statusReasonOptions = ['hardware_issue', 'software_issue', 'lost', 'other'].map(item => ({ label: item, value: item }));

    const confirmAssign = async () => {
        if (!assignTarget) return;
        await onAssign(assignEntityType, String(assignTarget.value));
        setShowAssign(false);
        setAssignTarget(null);
        if (assignEntityType === 'unit') {
            toggle();
        }
    };

    const confirmStatusChange = async () => {
        if (!pendingAction) return;
        await onSetStatus(pendingAction, reasonCategory || undefined, note || undefined, pendingAction !== "available" ? keepHolder : undefined);
        setPendingAction(null);
        setReasonCategory("");
        setNote("");
    };

    const openStatusForm = (action: StatusAction) => {
        setPendingAction(action);
        setReasonCategory("");
        setNote("");
        setKeepHolder(true);
    };

    return (
        <Modal isOpen={modal} toggle={toggle}>
            <ModalHeader className={"d-flex"}>
                <HeaderContainer>
                    <BackButton onClick={toggle} />
                    <h4 className={"pl-2"}>{unit.serial}</h4>
                </HeaderContainer>
            </ModalHeader>
            <ModalBody>
                <Block>
                    <StatusBadge status={unit.status}>{unit.status.replace("_", " ")}</StatusBadge>
                </Block>

                <Block mt={12}>
                    <FieldLabel as="p">Item</FieldLabel>
                    <div>{unit.itemName}</div>
                </Block>

                <Block mt={12}>
                    <FieldLabel as="p">Holder</FieldLabel>
                    <div>{unit.holderId ? `${unit.holderType}: ${unit.email}` : "—"}</div>
                </Block>

                <TabsNav>
                    <Tab active={detailTab === "attributes"} onClick={() => setDetailTab("attributes")}>
                        Attributes
                    </Tab>
                    <Tab active={detailTab === "actions"} onClick={() => setDetailTab("actions")}>
                        Actions
                    </Tab>
                    <Tab active={detailTab === "history"} onClick={() => setDetailTab("history")}>
                        History
                    </Tab>
                </TabsNav>

                {detailTab === "attributes" && (
                    <Block mt={16}>
                        <Block>
                            <FieldLabel>Asset tag</FieldLabel>
                            <Input
                                value={assetTagInput}
                                onChange={(e) => setAssetTagInput(e.target.value)}
                                placeholder="e.g. 0166"
                                error={!!assetTagError}
                            />
                            {assetTagError && <ErrorText>{assetTagError}</ErrorText>}
                        </Block>

                        {(unit.attributeFields ?? []).length > 0 && (
                            <Block mt={12}>
                                <FieldLabel as="p">Attributes</FieldLabel>
                                {unit.attributeFields.map((f) => (
                                    <Block mt={8} key={f.attributeKey}>
                                        {f.dataType === "bool" ? (
                                            <CheckboxRow>
                                                <Checkbox
                                                    type="checkbox"
                                                    checked={Boolean(attrValues[f.attributeKey])}
                                                    onChange={(e) => setAttrValue(f.attributeKey, e.target.checked)}
                                                />
                                                <CheckboxText>
                                                    {f.label} {f.isRequired && <RequiredMark>*</RequiredMark>}
                                                </CheckboxText>
                                            </CheckboxRow>
                                        ) : (
                                            <>
                                                <FieldLabel>
                                                    {f.label} {f.isRequired && <RequiredMark>*</RequiredMark>}
                                                </FieldLabel>
                                                {f.dataType === "list" ? (
                                                    <Select
                                                        value={(attrValues[f.attributeKey] as string) ?? ""}
                                                        onChange={(e) => setAttrValue(f.attributeKey, e.target.value)}
                                                    >
                                                        <option value="">—</option>
                                                        {f.options.map((o) => (
                                                            <option key={o} value={o}>
                                                                {o}
                                                            </option>
                                                        ))}
                                                    </Select>
                                                ) : (
                                                    <Input
                                                        type={f.dataType === "number" ? "number" : f.dataType === "date" ? "date" : "text"}
                                                        value={(attrValues[f.attributeKey] as string) ?? ""}
                                                        onChange={(e) => setAttrValue(f.attributeKey, e.target.value)}
                                                        mono={f.dataType === "number"}
                                                    />
                                                )}
                                            </>
                                        )}
                                    </Block>
                                ))}
                            </Block>
                        )}

                        <Block mt={12}>
                            <ButtonRow>
                                <PrimaryButton sm onClick={saveAttributes}>
                                    Save
                                </PrimaryButton>
                            </ButtonRow>
                            {saveError && <ErrorText>{saveError}</ErrorText>}
                        </Block>
                    </Block>
                )}

                {detailTab === "actions" && (
                    <Block mt={16}>
                        <ButtonRow>
                            {unit.status === "available" && (
                                <PrimaryButton sm onClick={() => setShowAssign((v) => !v)}>
                                    Assign
                                </PrimaryButton>
                            )}
                            {unit.status === "issued" && (
                                <>
                                    <PrimaryButton sm onClick={() => onRevoke()}>
                                        Revoke
                                    </PrimaryButton>
                                </>
                            )}
                            {unit.status !== "damaged" && (
                                <GhostButton sm onClick={() => openStatusForm("damaged")}>
                                    Mark damaged
                                </GhostButton>
                            )}
                            {unit.status !== "in_repair" && (
                                <GhostButton sm onClick={() => openStatusForm("in_repair")}>
                                    Mark in repair
                                </GhostButton>
                            )}
                            {unit.status === "in_repair" && (
                                <PrimaryButton sm onClick={() => openStatusForm("available")}>
                                    Mark repaired/available
                                </PrimaryButton>
                            )}
                            {unit.status === "damaged" && (
                                <>
                                    <GhostButton sm onClick={() => openStatusForm("discarded")}>
                                        Discard
                                    </GhostButton>
                                    <PrimaryButton sm onClick={() => openStatusForm("available")}>
                                        Mark repaired
                                    </PrimaryButton>
                                </>
                            )}
                            <GhostButton sm onClick={() => openStatusForm("discarded")}>
                                Mark discarded
                            </GhostButton>
                        </ButtonRow>

                        {showAssign && (
                            <InlineForm>
                                <div>
                                    <FieldLabel>Assign to</FieldLabel>
                                    <Select
                                        value={assignEntityType}
                                        onChange={(e) => {
                                            setAssignEntityType(e.target.value as EntityType);
                                            setAssignTarget(null);
                                        }}
                                    >
                                        <option value="employee">Employee</option>
                                        <option value="unit">Unit</option>
                                    </Select>
                                </div>
                                <div>
                                    <FieldLabel>{assignEntityType === "employee" ? "Employee" : "Unit"}</FieldLabel>
                                    <ReactSelect
                                        value={assignTarget}
                                        onChange={(option) => setAssignTarget(option as SelectOption)}
                                        options={targetOptions}
                                    />
                                </div>
                                <ButtonRow>
                                    <GhostButton sm onClick={() => setShowAssign(false)}>
                                        Cancel
                                    </GhostButton>
                                    <PrimaryButton sm onClick={confirmAssign} disabled={!assignTarget}>
                                        Confirm assign
                                    </PrimaryButton>
                                </ButtonRow>
                            </InlineForm>
                        )}

                        {pendingAction && (
                            <InlineForm>
                                {unit.status === "issued" && (
                                    <label>
                                        <input type="checkbox" checked={keepHolder} onChange={(e) => setKeepHolder(e.target.checked)} /> Keep current
                                        holder
                                    </label>
                                )}
                                <div>
                                    <FieldLabel>Reason (optional)</FieldLabel>
                                    <ReactSelect
                                        value={{
                                            label: reasonCategory,
                                            value: reasonCategory
                                        }}
                                        onChange={(option) => setReasonCategory(option.value)}
                                        options={statusReasonOptions}
                                    />
                                </div>
                                <div>
                                    <FieldLabel>Note (optional)</FieldLabel>
                                    <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Additional detail" />
                                </div>
                                <ButtonRow>
                                    <GhostButton sm onClick={() => setPendingAction(null)}>
                                        Cancel
                                    </GhostButton>
                                    <PrimaryButton sm onClick={confirmStatusChange}>
                                        Confirm
                                    </PrimaryButton>
                                </ButtonRow>
                            </InlineForm>
                        )}
                    </Block>
                )}

                {detailTab === "history" && (
                    <Block mt={16}>
                        {_.get(unit, 'events.length', 0) === 0 ? (
                            <div>No history yet.</div>
                        ) : (
                            <HistoryList>
                                {[...unit.events].reverse().map((ev, i) => (
                                    <HistoryItem key={ev.id ?? i}>
                                        <HistoryText>
                                            {/* @ts-ignore */}
                                            {EVENT_LABELS[ev.eventType] ?? ev.eventType}
                                            {ev.fromStatus && ev.toStatus ? ` — ${ev.fromStatus} → ${ev.toStatus}` : ""}
                                        </HistoryText>
                                        <HistoryMeta>
                                            {moment.utc(ev.createdAt).local().format('MM/DD/YYYY HH:mm')}
                                            {/* @ts-ignore */}
                                            {ev.entityType && ev.entityId ? ` · ${ev.entityType}:${ev.entityId}` : ""}
                                            {ev.note ? ` · ${ev.note}` : ""}
                                        </HistoryMeta>
                                    </HistoryItem>
                                ))}
                            </HistoryList>
                        )}
                    </Block>
                )}
            </ModalBody>
        </Modal>
    );
}
